import { useCallback, useRef, useEffect } from 'react';
import { t } from '@lingui/macro';
import { canUseDOM, useDisclosure, useEnhancedEffect } from '@landr/maestro';
import { FiltersParams } from 'types/params';
import { ModifiersPayload } from 'controllers/SearchController/types';
import {
    ContentSuggestionPayload,
    isContentSuggestionPayload,
    isModifiersPayload,
} from '../types';
import { useNavigateToContent } from './useNavigateToContent';
import useWaitForClose from './useWaitForClose';

const useSearchBar = ({
    addFilter,
    searchControllerRemoveFilter,
    clearAll,
    clearTextInput,
    resetSelectedSuggestion,
    hasTextInputValue,
    isMobile,
    inputValue,
    predictiveModifiers,
}: {
    addFilter: (modifiers: ModifiersPayload[]) => void;
    searchControllerRemoveFilter: (modifiers: ModifiersPayload[]) => void;
    clearAll: () => void;
    clearTextInput: () => void;
    hasTextInputValue: boolean;
    isMobile: boolean;
    resetSelectedSuggestion: () => void;
    inputValue: string;
    predictiveModifiers?: ModifiersPayload | ContentSuggestionPayload;
}) => {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const { navigateToContent } = useNavigateToContent();

    const {
        isOpen: isSuggestionsOpen,
        onOpen: openSuggestions,
        onClose,
    } = useDisclosure();

    const executeAfterClose = useWaitForClose(isSuggestionsOpen);

    const closeSuggestions = useCallback(() => {
        onClose();
        resetSelectedSuggestion();
    }, [resetSelectedSuggestion, onClose]);

    // close suggestion on window resize
    useEffect(() => {
        const handleResize = () => {
            closeSuggestions();
            inputRef?.current?.blur();
        };

        !isMobile && window.addEventListener('resize', handleResize);
        return () => {
            !isMobile && window.removeEventListener('resize', handleResize);
        };
    }, [closeSuggestions, isMobile]);

    const applySuggestion = useCallback(
        (payload?: ModifiersPayload | ContentSuggestionPayload) => {
            if (!payload) {
                return;
            }

            closeSuggestions();
            executeAfterClose(() => {
                inputRef?.current?.blur();
                clearTextInput();

                if (isModifiersPayload(payload)) {
                    addFilter([payload]);
                } else if (isContentSuggestionPayload(payload)) {
                    navigateToContent(payload);
                }
            });
        },
        [
            addFilter,
            clearTextInput,
            closeSuggestions,
            navigateToContent,
            executeAfterClose,
        ],
    );

    const removeFilter = useCallback(
        (modifiers: ModifiersPayload[]) => {
            closeSuggestions();

            // Wait for suggestions to actually close before removing filter, this will prevent flickering in the UI
            executeAfterClose(() => {
                inputRef?.current?.blur();
                searchControllerRemoveFilter(modifiers);
            });
        },
        [searchControllerRemoveFilter, closeSuggestions, executeAfterClose],
    );

    const handleClearCta = useCallback(() => {
        if (hasTextInputValue) {
            clearTextInput();
        } else {
            clearAll();
        }
    }, [hasTextInputValue, clearTextInput, clearAll]);

    const clearCtaLabel = hasTextInputValue ? t`Clear` : t`Clear all`;

    useEnhancedEffect(() => {
        // The following handler is to get the actual height of the viewport.
        // On iOS, the keyboard will cover the modal when opened.
        // The hidden content won't be scrollable because it's not considered an overflow
        if (!canUseDOM || !window?.visualViewport) return;

        function viewportHandler(event: Event) {
            const viewport = event.target as VisualViewport;

            document.documentElement.style.setProperty(
                '--viewport-height',
                `${Math.floor(viewport.height)}px`,
            );
        }

        window.visualViewport.addEventListener('resize', viewportHandler);
        return () => {
            window?.visualViewport?.removeEventListener(
                'resize',
                viewportHandler,
            );
        };
    });

    const handleFocus = useCallback(() => {
        if (!isSuggestionsOpen) {
            isMobile && inputRef?.current?.blur();
            openSuggestions();
        }
    }, [isSuggestionsOpen, isMobile, openSuggestions]);

    const handleApplyTextSearch = useCallback(() => {
        if (predictiveModifiers) {
            applySuggestion(predictiveModifiers);
        } else if (inputValue !== '') {
            applySuggestion([FiltersParams.textsearch, inputValue]);
        }
    }, [applySuggestion, inputValue, predictiveModifiers]);

    return {
        clearCtaLabel,
        inputRef,
        isSuggestionsOpen,
        applySuggestion,
        closeSuggestions,
        handleClearCta,
        handleFocus,
        removeFilter,
        handleApplyTextSearch,
    };
};

export default useSearchBar;
